import { MARGIN_X, RULE_SPACING, lineY } from "../notebookGrid";
import { StudentInkCluster } from "./studentInkClusters";

/**
 * What gets stamped onto a rendered screenshot so the agent can tie the scene
 * digest to the picture: labels on the student's handwriting, and line numbers
 * down the gutter.
 *
 * Both have to be drawn HERE, onto the exported bitmap, rather than rendered on
 * the canvas for the export to pick up. tldraw's exporter (`getSvgJsx`) paints
 * shapes over a flat background rect and nothing else — the `OnTheCanvas` slot
 * that holds the ruled paper is a React overlay the student sees and the export
 * does not. Anything the agent is supposed to see in the screenshot has to be
 * composited in after the render.
 */

/**
 * Draw the ink-cluster labels onto a rendered screenshot ("set-of-mark").
 *
 * The agent references handwriting by name and reads its CONTENT off the
 * screenshot — but with OCR off, nothing in the prompt says which name belongs to
 * which marks on the page. The scene digest describes each cluster's position
 * (rule line, side of the column, what agent content it sits under), which the
 * agent can match up by reasoning; a label drawn on the image itself removes the
 * reasoning step entirely.
 *
 * Cheap enough to run in-turn: one composite of the already-rendered image plus a
 * few dozen fills, tens of milliseconds, all before the request is sent. It has
 * to be in-turn — a label that arrives after the LLM call is a label for a
 * question the agent has already answered.
 *
 * INVARIANT: nothing drawn here paints over a student stroke. A label is a dark
 * plate carrying white glyphs — it has to be, to stay readable after JPEG and
 * the provider's downscale — so a label on top of writing takes that writing out
 * of the only copy the agent gets, and takes it silently: the digest never says
 * what handwriting READS, so there is no second source to recover it from. Every
 * label is therefore placed in space no cluster occupies, and a label with
 * nowhere to go is dropped rather than parked on the ink. See `placeBadge`.
 *
 * The plate is also drawn slightly translucent (`BADGE_PLATE`), which is a
 * second line of defence and not a substitute for the first: 80% violet over a
 * pencil stroke leaves something visible, but not something you would want to
 * read a digit off.
 */

/**
 * Bracket colour. Opaque: it is a 2px dashed line that has to survive JPEG and
 * the provider's downscale, and there is nothing behind it worth seeing — the
 * bracket is drawn outside the writing, on blank paper.
 */
const BADGE_BRACKET = "#4527A0";
/**
 * The plate behind a label — the same violet, held just short of opaque.
 *
 * Placement already keeps every label off the handwriting (see `placeBadge`),
 * so this is the second layer, not the first: it covers what placement doesn't
 * reason about (agent-drawn content, ruled lines, the printed sheet) and it
 * bounds the damage if a future change to the geometry gets a placement wrong.
 * Anything under a label is dimmed and tinted rather than deleted.
 *
 * 0.8 is where both halves still work. Over white paper the plate composites to
 * ~#664EB1, which holds a 6.3:1 contrast ratio against the white glyphs — well
 * clear of the 4.5:1 the text needs to stay readable after JPEG and the
 * provider's downscale, which is the whole reason the label is a filled plate
 * and not bare text. Much below ~0.7 the glyphs start to fight whatever they
 * are sitting on; at 1.0 nothing shows through at all.
 */
const BADGE_PLATE = "rgba(69, 39, 160, 0.8)";
/**
 * Bracket for handwriting touched since the model last looked. Amber because
 * hue distance survives JPEG and the provider's resample; the caption names
 * every amber id too, so the colour is never the only signal.
 */
const CHANGED_BRACKET = "#B45309";
/** Same amber, same near-opacity as the violet plate. */
const CHANGED_PLATE = "rgba(180, 83, 9, 0.8)";
/**
 * Label text colour. Opaque — the glyphs are the one thing on this image that
 * must not blur, and they are what the plate exists to make readable.
 */
const BADGE_TEXT = "#FFFFFF";
/**
 * Label text size in OUTPUT pixels, i.e. after the screenshot's downscale.
 *
 * Deliberately absolute rather than a fraction of the writing: the label has to
 * survive JPEG encoding and the provider's own image downscale, and small text is
 * the first thing either destroys. Vision models tile images at roughly this
 * scale, so anything much under ~14px stops being reliably readable.
 */
const BADGE_FONT_PX = 18;
const BADGE_PAD_X = 6;
const BADGE_PAD_Y = 3;
const BRACKET_WIDTH = 2;
/**
 * Clear air between the outermost ink pixel and the innermost bracket pixel.
 *
 * Not touching is a stronger requirement than not overlapping, and the one that
 * matters at this size: a dashed line laid right along the edge of a glyph
 * merges with it under JPEG ringing and the provider's downscale, so a `4` picks
 * up a closed top and a `1` grows a base. Four pixels survives both.
 */
const BRACKET_CLEARANCE = 4;
/**
 * How far outside the ink's bounding box the dashed bracket's PATH is drawn.
 *
 * The bounds are TIGHT — `computeStudentInkClusters` measures the ink as it is
 * rendered, stroke width and all, so the outermost pixels touch the edge — and
 * `strokeRect` centres its line on the path, so half the bracket width lands
 * inside wherever the path is put. Clearance plus that half-width is what
 * actually separates the two.
 */
const BRACKET_OUTSET = BRACKET_CLEARANCE + BRACKET_WIDTH / 2;
/** Gap between the bracket and the label parked outside it. */
const BADGE_GAP = 2;
/**
 * Beyond this many clusters the image is more label than picture, whatever the
 * placement finds room for. The digest still names every cluster and describes
 * where it is, so the text path degrades gracefully; per-line clustering means a
 * viewport with this much handwriting in it is unusual.
 */
const MAX_BADGES = 40;

export interface BadgeGeometry {
  /** Screenshot bounds in page space — what the render covered. */
  contextBounds: { x: number; y: number; w: number; h: number };
  /** Page-to-output scale the screenshot was rendered at. */
  scale: number;
  /** Output image size in px, for clamping labels inside the frame. */
  outputW: number;
  outputH: number;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Grow a rect by `by` on every side. */
function inflate(r: Rect, by: number): Rect {
  return { x: r.x - by, y: r.y - by, w: r.w + 2 * by, h: r.h + 2 * by };
}

/** Do two rects share any area? Touching edges don't count. */
function overlaps(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
  );
}

/**
 * Axis separation between two rects: how far apart they'd have to be inflated
 * before they met. 0 when they overlap on both axes.
 */
function separation(a: Rect, b: Rect): number {
  const dx = Math.max(0, Math.max(a.x - (b.x + b.w), b.x - (a.x + a.w)));
  const dy = Math.max(0, Math.max(a.y - (b.y + b.h), b.y - (a.y + a.h)));
  return Math.max(dx, dy);
}

/**
 * How far this cluster's bracket may stand off its ink — normally
 * BRACKET_OUTSET, less when a neighbour is close enough to be hit.
 *
 * Clearance is in OUTPUT pixels while the gap between two clusters is in page
 * pixels, so the picture changes with `scale`: clusters sit at least 20 page px
 * apart, which is 20 output px on a 1:1 shot and 6 on a heavily downscaled one.
 * A fixed standoff that is comfortable at 1:1 reaches straight through a
 * neighbour's writing at 0.3. Give up the standoff instead of the guarantee —
 * a bracket drawn a little tight is worth strictly more than one drawn across
 * the digits next door.
 */
function bracketOutset(ink: Rect, all: Rect[]): number {
  let outset = BRACKET_OUTSET;
  for (const other of all) {
    if (other === ink) continue;
    const room = separation(ink, other) / 2 - BRACKET_WIDTH / 2;
    if (room < outset) outset = Math.max(0, room);
  }
  return outset;
}

/**
 * Where a label may sit, best first: hugging the writing it names, and always
 * OUTSIDE the bracket rather than over it.
 *
 * Above-first preserves the reading order the previous version had. Left-of
 * comes last because on ruled paper the space left of the writing is the
 * gutter, which `drawGutterRuler` fills with line numbers; the space to the
 * right is usually the empty tail of the line.
 */
function badgeSpots(box: Rect, w: number, h: number): Rect[] {
  const above = box.y - BADGE_GAP - h;
  const below = box.y + box.h + BADGE_GAP;
  const before = box.x - BADGE_GAP - w;
  const after = box.x + box.w + BADGE_GAP;
  const startAligned = box.x;
  const endAligned = box.x + box.w - w;
  const topAligned = box.y;
  const bottomAligned = box.y + box.h - h;
  return [
    { x: startAligned, y: above, w, h },
    { x: endAligned, y: above, w, h },
    { x: startAligned, y: below, w, h },
    { x: endAligned, y: below, w, h },
    { x: after, y: topAligned, w, h },
    { x: after, y: bottomAligned, w, h },
    { x: before, y: topAligned, w, h },
    { x: before, y: bottomAligned, w, h },
  ];
}

/**
 * Pick a spot for one label, or null when the writing around it leaves none.
 *
 * The label is a dark plate by design — it has to survive JPEG and the
 * provider's downscale — so anything it lands on is at best a smudge in the
 * agent's view, translucency notwithstanding. The ink is the thing the agent is
 * here to read and the digest cannot substitute for (the digest says where
 * handwriting is, never what it says), so ink outranks labels absolutely: a spot
 * that covers any stroke is not a spot.
 * Returning null costs one id-to-marks shortcut on a page that still carries its
 * bracket-free description in the digest; the alternative costs the writing.
 *
 * `keepOut` and `placedBadges` are two lists rather than one because only the
 * second changes as the loop runs: concatenating them per cluster would copy
 * every cluster's keep-out rect once per cluster, on a path the header calls
 * out as in-turn.
 */
function placeBadge(
  box: Rect,
  w: number,
  h: number,
  outputW: number,
  outputH: number,
  keepOut: Rect[],
  placedBadges: Rect[],
): Rect | null {
  if (w > outputW || h > outputH) return null;

  const clear = (spot: Rect) =>
    !keepOut.some((k) => overlaps(spot, k)) &&
    !placedBadges.some((b) => overlaps(spot, b));
  const inFrame = (spot: Rect) =>
    spot.x >= 0 &&
    spot.y >= 0 &&
    spot.x + spot.w <= outputW &&
    spot.y + spot.h <= outputH;

  const spots = badgeSpots(box, w, h);
  for (const spot of spots) {
    if (inFrame(spot) && clear(spot)) return spot;
  }
  // Nothing fit as-is. Slide the same spots back inside the frame and retry —
  // this is what rescues writing hard against an edge. Sliding can push a label
  // onto ink, which is exactly what `clear` is here to reject.
  for (const spot of spots) {
    const slid = {
      ...spot,
      x: Math.min(Math.max(spot.x, 0), outputW - w),
      y: Math.min(Math.max(spot.y, 0), outputH - h),
    };
    if (clear(slid)) return slid;
  }
  return null;
}

/**
 * Outline each cluster and stamp its label. Mutates the canvas context; returns
 * the labels it actually drew — a badge with nowhere to go is dropped.
 *
 * A cluster is boxed only if its label also found a home: an unnamed bracket
 * invites the agent to attach whichever id is nearest, which is the mis-mapping
 * the whole set-of-mark scheme exists to prevent.
 */
export function drawInkBadges(
  ctx: CanvasRenderingContext2D,
  clusters: StudentInkCluster[],
  geometry: BadgeGeometry,
): string[] {
  if (clusters.length === 0) return [];

  const { contextBounds, scale, outputW, outputH } = geometry;
  ctx.save();
  ctx.font = `bold ${BADGE_FONT_PX}px system-ui, -apple-system, sans-serif`;
  ctx.textBaseline = "top";
  ctx.lineWidth = BRACKET_WIDTH;
  // strokeStyle is set per bracket below: violet, or amber if just touched.

  const inked: { label: string; ink: Rect; changed: boolean }[] = [];
  for (const cluster of clusters) {
    const ink: Rect = {
      x: (cluster.bounds.x - contextBounds.x) * scale,
      y: (cluster.bounds.y - contextBounds.y) * scale,
      w: cluster.bounds.w * scale,
      h: cluster.bounds.h * scale,
    };
    // Fully outside the rendered frame — the shape filter and this geometry are
    // derived from the same bounds, so this is belt-and-braces.
    if (
      ink.x + ink.w < 0 ||
      ink.y + ink.h < 0 ||
      ink.x > outputW ||
      ink.y > outputH
    ) {
      continue;
    }
    inked.push({ label: cluster.label, ink, changed: cluster.changed });
  }

  const visible: { label: string; box: Rect; changed: boolean }[] = [];
  /**
   * No-go areas for every label, whether or not that cluster gets labelled
   * itself: ink, its bracket, and the bracket's stroke width. Built from ALL
   * clusters in frame, so a dropped or capped cluster still protects its own
   * writing from someone else's label.
   */
  const keepOut: Rect[] = [];

  // Hoisted: `bracketOutset` scans every cluster's ink for each cluster, so
  // building this inside the loop would allocate the whole list N times over.
  const allInk = inked.map((e) => e.ink);
  for (const { label, ink, changed } of inked) {
    const outset = bracketOutset(ink, allInk);
    keepOut.push(inflate(ink, outset + BRACKET_WIDTH / 2));
    // Dashed box: says exactly how much writing the label refers to. Without it a
    // label sitting between two lines is ambiguous about which one it names.
    visible.push({ label, box: inflate(ink, outset), changed });
  }

  const placed: { label: string; box: Rect; badge: Rect; changed: boolean }[] =
    [];
  // Labels avoid each other too. Two stacked labels are two unreadable labels,
  // and an unreadable id is indistinguishable from a wrong one. Grown in place
  // and passed alongside `keepOut` rather than concatenated with it each pass.
  const placedBadges: Rect[] = [];
  for (const { label, box, changed } of visible) {
    if (placed.length >= MAX_BADGES) break;
    const badgeW = ctx.measureText(label).width + 2 * BADGE_PAD_X;
    const badgeH = BADGE_FONT_PX + 2 * BADGE_PAD_Y;
    const badge = placeBadge(
      box,
      badgeW,
      badgeH,
      outputW,
      outputH,
      keepOut,
      placedBadges,
    );
    if (!badge) continue;
    placed.push({ label, box, badge, changed });
    placedBadges.push(badge);
  }

  // Brackets first, labels second: a bracket drawn later would dash across a
  // label that had already been stamped in the space beside it.
  ctx.setLineDash([6, 4]);
  for (const { box, changed } of placed) {
    ctx.strokeStyle = changed ? CHANGED_BRACKET : BADGE_BRACKET;
    ctx.strokeRect(box.x, box.y, box.w, box.h);
  }
  ctx.setLineDash([]);

  for (const { label, badge, changed } of placed) {
    // Plate and bracket move together, or a changed cluster reads half-marked.
    ctx.fillStyle = changed ? CHANGED_PLATE : BADGE_PLATE;
    ctx.fillRect(badge.x, badge.y, badge.w, badge.h);
    ctx.fillStyle = BADGE_TEXT;
    ctx.fillText(label, badge.x + BADGE_PAD_X, badge.y + BADGE_PAD_Y);
  }

  ctx.restore();
  return placed.map((p) => p.label);
}

/** Whether more clusters exist than `drawInkBadges` will label. */
export function badgesTruncated(clusterCount: number): boolean {
  return clusterCount > MAX_BADGES;
}

/** Gutter line numbers: muted, matching the printed sheet's own numbering. */
const RULER_COLOR = "rgba(90,72,36,0.75)";
const RULER_FONT_PX = 15;
/** Gap between a number's right edge and the red margin. */
const RULER_INSET = 8;
/**
 * Don't number lines closer together than this in OUTPUT pixels — on a heavily
 * downscaled screenshot consecutive numbers would collide into a smear.
 */
const RULER_MIN_SPACING_PX = 22;

/**
 * Number the rule lines down the left gutter of the screenshot, matching the
 * numbers printed on the student's own sheet.
 *
 * This is the other half of the digest's `line 12` — a shared coordinate frame
 * costs nothing per turn and keeps working when the ink labels don't (too many
 * clusters to label, or handwriting the clusterer grouped differently than the
 * agent reads it). Returns how many numbers were drawn.
 *
 * Skipped entirely when the gutter is not in frame: the agent's camera can be
 * panned to a column further right, and inventing a ruler at the left edge of
 * whatever is on screen would both overlap content and disagree with the sheet
 * the student is looking at.
 */
export function drawGutterRuler(
  ctx: CanvasRenderingContext2D,
  geometry: BadgeGeometry,
): number {
  const { contextBounds, scale, outputH } = geometry;
  // Gutter runs from x=0 to the red margin, in page space.
  if (contextBounds.x > MARGIN_X - RULER_INSET) return 0;
  if (RULE_SPACING * scale < RULER_MIN_SPACING_PX) return 0;

  const gutterRight = (MARGIN_X - contextBounds.x - RULER_INSET) * scale;
  if (gutterRight <= 0) return 0;

  ctx.save();
  ctx.font = `${RULER_FONT_PX}px system-ui, -apple-system, sans-serif`;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "right";
  ctx.fillStyle = RULER_COLOR;

  const first = Math.max(
    1,
    Math.ceil((contextBounds.y - lineY(0)) / RULE_SPACING),
  );
  let drawn = 0;
  for (let n = first; ; n++) {
    const y = (lineY(n) - contextBounds.y) * scale;
    if (y > outputH) break;
    // Baseline sits ON the rule, exactly as the printed sheet has it.
    if (y >= RULER_FONT_PX) {
      ctx.fillText(String(n), gutterRight, y);
      drawn += 1;
    }
  }

  ctx.restore();
  return drawn;
}
